<script>
import { format, parseISO } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { useVuelidate } from '@vuelidate/core';
import { emitter } from 'shared/helpers/mitt';

import NextButton from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
    NextButton,
  },
  props: {
    label: { type: String, required: true },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: [String, Number], default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
  },
  emits: ['update', 'delete', 'copy'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
    };
  },
  computed: {
    normalizedDisplayType() {
      const DISPLAY_TYPE_KEYS = [
        'text',
        'number',
        'currency',
        'percent',
        'link',
        'date',
        'list',
        'checkbox',
      ];
      const raw = this.attributeType;
      if (typeof raw === 'number') {
        return DISPLAY_TYPE_KEYS[raw] || 'text';
      }
      return String(raw || 'text').toLowerCase();
    },
    displayValue() {
      if (this.isAttributeTypeDate) {
        return this.value
          ? new Date(this.value || new Date()).toLocaleDateString()
          : '---';
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.hasValue ? this.value : '---';
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.normalizedDisplayType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.normalizedDisplayType === 'list';
    },
    isAttributeTypeLink() {
      return this.normalizedDisplayType === 'link';
    },
    isAttributeTypeDate() {
      return this.normalizedDisplayType === 'date';
    },
    isAttributeTypeNumber() {
      return ['number', 'currency', 'percent'].includes(
        this.normalizedDisplayType
      );
    },
    useTextareaForEdit() {
      return (
        !this.isAttributeTypeDate &&
        !this.isAttributeTypeNumber &&
        !this.isAttributeTypeCheckbox &&
        !this.isAttributeTypeList
      );
    },
    hasValue() {
      return this.value !== null && this.value !== '';
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    valueSegments() {
      if (!this.hasValue || this.isAttributeTypeCheckbox) return null;
      const raw = String(this.value).trim();
      if (!raw || raw === '---') return null;

      if (raw.includes('🏢')) {
        const parts = raw.split(/(?=🏢)/).map(s => s.trim()).filter(Boolean);
        if (parts.length > 1) return parts;
      }

      if (raw.length > 48 && /[,\n]/.test(raw)) {
        const parts = raw
          .split(/[,\n]+/)
          .map(s => s.trim())
          .filter(Boolean);
        if (parts.length > 1) return parts;
      }

      return null;
    },
    inputType() {
      if (this.isAttributeTypeLink) return 'url';
      if (this.isAttributeTypeNumber) return 'number';
      if (this.isAttributeTypeDate) return 'date';
      return 'text';
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url?.$invalid) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      if (this.v$.editedValue.regexValidation?.$invalid) {
        return (
          this.regexCue ||
          this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT')
        );
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          if (!this.attributeRegex || !value) return true;
          try {
            return getRegexp(this.attributeRegex).test(value);
          } catch {
            return false;
          }
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      const field = this.$refs.inputfield;
      if (!field) return;
      const el = Array.isArray(field) ? field[0] : field;
      el?.focus?.();
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
    onEditFieldKeydown(event) {
      if (!this.useTextareaForEdit || event.key !== 'Enter') return;
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        this.onUpdate();
      }
    },
    onUpdate() {
      const updatedValue = this.isAttributeTypeDate
        ? parseISO(this.editedValue)
        : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
  },
};
</script>

<template>
  <div class="group/attr w-full px-3 py-3">
    <div class="flex items-center mb-2 min-h-5">
      <h4 class="flex items-center w-full m-0 text-sm">
        <div v-if="isAttributeTypeCheckbox" class="flex items-center">
          <input
            v-model="editedValue"
            class="!my-0 ltr:mr-2 ltr:ml-0 rtl:mr-0 rtl:ml-2"
            type="checkbox"
            @change="onUpdate"
          />
        </div>
        <div class="flex items-center justify-between w-full gap-2 min-h-5">
          <div
            class="inline-flex items-center gap-1.5 min-w-0 flex-1"
            :class="
              v$.editedValue.$error ? 'text-n-ruby-11' : 'text-n-slate-9'
            "
          >
            <span
              class="text-[11px] font-medium uppercase tracking-wide leading-none"
            >
              {{ label }}
            </span>
            <HelperTextPopup
              v-if="description"
              :message="description"
            />
          </div>
          <div
            v-if="showActions"
            class="flex items-center gap-0.5 flex-shrink-0"
          >
            <template v-if="valueSegments">
              <NextButton
                v-if="hasValue"
                v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
                xs
                slate
                ghost
                icon="i-lucide-clipboard"
                class="opacity-0 pointer-events-none group-hover/attr:opacity-100 group-hover/attr:pointer-events-auto transition-opacity"
                @click="onCopy"
              />
              <NextButton
                v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
                xs
                slate
                ghost
                icon="i-lucide-pen"
                class="opacity-0 pointer-events-none group-hover/attr:opacity-100 group-hover/attr:pointer-events-auto transition-opacity"
                @click="onEdit"
              />
            </template>
            <NextButton
              v-if="hasValue"
              v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
              slate
              sm
              link
              icon="i-lucide-trash-2"
              @click="onDelete"
            />
          </div>
        </div>
      </h4>
    </div>
    <div v-if="notAttributeTypeCheckboxAndList">
      <div v-if="isEditing" v-on-clickaway="onClickAway">
        <div
          class="flex w-full mb-2"
          :class="useTextareaForEdit ? 'items-start' : 'items-center'"
        >
          <textarea
            v-if="useTextareaForEdit"
            ref="inputfield"
            v-model="editedValue"
            rows="4"
            class="flex-1 min-h-[5rem] resize-y ltr:!rounded-r-none rtl:!rounded-l-none !mb-0 !text-sm !py-2 !px-3 whitespace-pre-wrap"
            autofocus="true"
            :class="{ error: v$.editedValue.$error }"
            @blur="v$.editedValue.$touch"
            @keydown="onEditFieldKeydown"
          />
          <input
            v-else
            ref="inputfield"
            v-model="editedValue"
            :type="inputType"
            class="flex-1 !h-8 ltr:!rounded-r-none rtl:!rounded-l-none !mb-0 !text-sm"
            autofocus="true"
            :class="{ error: v$.editedValue.$error }"
            @blur="v$.editedValue.$touch"
            @keyup.enter="onUpdate"
          />
          <div :class="useTextareaForEdit ? 'self-start' : ''">
            <NextButton
              sm
              icon="i-lucide-check"
              class="ltr:rounded-l-none rtl:rounded-r-none"
              :class="useTextareaForEdit ? 'h-[5rem]' : 'h-[34px]'"
              @click="onUpdate"
            />
          </div>
        </div>
        <span
          v-if="shouldShowErrorMessage"
          class="block w-full -mt-px text-sm font-normal text-n-ruby-11"
        >
          {{ errorMessage }}
        </span>
      </div>
      <div
        v-show="!isEditing"
        class="relative w-full rounded-lg bg-n-slate-2/70 px-3 py-2.5"
        :class="{ 'is-editable': showActions }"
      >
        <div
          v-if="valueSegments"
          class="flex flex-col gap-1.5 min-w-0 w-full"
        >
          <div
            v-for="(segment, index) in valueSegments"
            :key="index"
            class="w-full min-w-0 rounded-lg bg-n-solid-2 px-2.5 py-1.5 text-sm font-medium leading-snug text-n-slate-12 outline outline-1 outline-n-weak -outline-offset-1 break-words"
          >
            {{ segment }}
          </div>
        </div>
        <a
          v-else-if="isAttributeTypeLink"
          :href="hrefURL"
          target="_blank"
          rel="noopener noreferrer"
          class="block min-w-0 mb-0 text-sm font-medium break-words leading-relaxed text-n-blue-11 hover:underline"
        >
          {{ urlValue }}
        </a>
        <p
          v-else
          class="min-w-0 mb-0 text-sm font-medium break-words leading-relaxed text-n-slate-12"
          :class="{ 'italic text-n-slate-9': !hasValue }"
        >
          {{ displayValue }}
        </p>
        <div
          v-if="showActions && !valueSegments"
          class="absolute top-2 ltr:right-2 rtl:left-2 z-10 flex items-center gap-0.5 opacity-0 pointer-events-none group-hover/attr:opacity-100 group-hover/attr:pointer-events-auto transition-opacity"
        >
          <NextButton
            v-if="hasValue"
            v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
            xs
            slate
            ghost
            icon="i-lucide-clipboard"
            @click="onCopy"
          />
          <NextButton
            v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
            xs
            slate
            ghost
            icon="i-lucide-pen"
            @click="onEdit"
          />
        </div>
      </div>
    </div>
    <div v-if="isAttributeTypeList">
      <MultiselectDropdown
        :options="listOptions"
        :selected-item="selectedItem"
        :has-thumbnail="false"
        :multiselector-placeholder="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
        "
        :no-search-result="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
        "
        :input-placeholder="
          $t(
            'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
          )
        "
        @select="onUpdateListValue"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.selector-wrap) {
  @apply m-0 top-1;

  .selector-name {
    @apply ml-0;
  }
}

:deep(.name) {
  @apply ml-0;
}
</style>
